<template>
    <Teleport to="body">
        <div
            v-if="isVisible && mode === 'panel'"
            class="fixed inset-x-3 bottom-3 top-16 z-[60] flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-950/20 ring-1 ring-slate-950/5 sm:left-auto sm:right-6 sm:top-auto sm:h-[min(620px,calc(100vh-4rem))] sm:w-[420px] lg:inset-y-0 lg:left-auto lg:right-0 lg:top-0 lg:h-auto lg:w-[420px] lg:rounded-none lg:border-y-0 lg:border-l lg:border-r-0 lg:shadow-none lg:ring-0"
            role="dialog"
            :aria-label="t('tamepulse.chat.floatingTitle')"
        >
            <header class="flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 bg-white px-4 py-3">
                <div class="min-w-0">
                    <div class="truncate text-sm font-semibold text-slate-950">{{ t('tamepulse.chat.floatingTitle') }}</div>
                    <div class="truncate text-xs text-slate-500">{{ t('tamepulse.chat.floatingSubtitle') }}</div>
                </div>

                <div class="flex shrink-0 items-center gap-1">
                    <button
                        type="button"
                        class="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                        :aria-label="t('tamepulse.chat.floatingExpand')"
                        :title="t('tamepulse.chat.floatingExpand')"
                        @click="expandPanel"
                    >
                        <fa-icon icon="expand" />
                    </button>
                    <button
                        type="button"
                        class="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                        :aria-label="t('tamepulse.chat.floatingClose')"
                        :title="t('tamepulse.chat.floatingClose')"
                        @click="closePanel"
                    >
                        <fa-icon icon="xmark" />
                    </button>
                </div>
            </header>

            <div class="min-h-0 flex-1">
                <TamepulseChatPanel variant="compact" />
            </div>
        </div>

        <div
            v-else-if="isVisible && mode === 'expanded'"
            class="fixed inset-0 z-[70] bg-slate-950/25 p-0 sm:p-6 lg:inset-y-0 lg:left-[var(--tamepulse-chat-workspace-left)] lg:right-0 lg:bg-transparent lg:p-0"
            :style="{ '--tamepulse-chat-workspace-left': workspaceOffset }"
            role="dialog"
            :aria-label="t('tamepulse.chat.floatingTitle')"
        >
            <div class="ml-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-none border border-slate-200/90 bg-white shadow-2xl shadow-slate-950/20 ring-1 ring-slate-950/5 sm:rounded-xl lg:ml-0 lg:max-w-none lg:rounded-none lg:border-y-0 lg:border-r-0 lg:shadow-none lg:ring-0">
                <header class="flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 bg-white px-4 py-3">
                    <div class="min-w-0">
                        <div class="truncate text-sm font-semibold text-slate-950">{{ t('tamepulse.chat.floatingTitle') }}</div>
                        <div class="truncate text-xs text-slate-500">{{ t('tamepulse.chat.floatingSubtitle') }}</div>
                    </div>

                    <div class="flex shrink-0 items-center gap-1">
                        <button
                            type="button"
                            class="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                            :aria-label="t('tamepulse.chat.floatingCollapse')"
                            :title="t('tamepulse.chat.floatingCollapse')"
                            @click="openPanel"
                        >
                            <fa-icon icon="compress" />
                        </button>
                        <button
                            type="button"
                            class="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                            :aria-label="t('tamepulse.chat.floatingClose')"
                            :title="t('tamepulse.chat.floatingClose')"
                            @click="closePanel"
                        >
                            <fa-icon icon="xmark" />
                        </button>
                    </div>
                </header>

                <div class="min-h-0 flex-1 p-3 sm:p-4">
                    <TamepulseChatPanel variant="expanded" />
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, watch } from 'vue'
import { gql } from 'graphql-tag'
import { useRoute } from 'vue-router'
import { useQuery } from '@/composables/useGlobals'
import TamepulseChatPanel from '@/components/admin/tamepulse/chat/TamepulseChatPanel.vue'
import { useTamepulseChatWidget } from '@/composables/useTamepulseChatWidget'

const { t } = useI18n()
const route = useRoute()
const {
    mode,
    workspaceOffset,
    openPanel,
    expandPanel,
    closePanel,
    setWidgetVisible,
} = useTamepulseChatWidget()

const { result } = useQuery(gql`
    query TamepulseChatWidgetTenant {
        currentTenant {
            id
            type
            internal_chat_enabled
        }
    }
`, {}, { fetchPolicy: 'network-only' })

const isVisible = computed(() => {
    return result.value?.currentTenant?.type === 'tamepulse'
        && result.value.currentTenant.internal_chat_enabled === true
        && !String(route.name || '').startsWith('tamepulse.chat.')
})

watch(isVisible, (value) => {
    setWidgetVisible(value)
}, { immediate: true })

watch(() => result.value?.currentTenant?.internal_chat_enabled, enabled => {
    if (enabled === false) closePanel()
}, { immediate: true })
</script>
